import Select from '@/components/form/select';
import Text from '@/components/text';
import { FormHandle } from '@/hooks/use-form-handle.hook';
import {
  getMetadataControlActionOptions,
  getMetadataToggleActionOptions,
  MetadataControlsSettings,
  MetadataControlType,
} from '@/lib/models/policy-model-v1/metadata-controls';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import { Box, Stack, Theme } from '@mui/material';
import { cloneDeep } from 'lodash';
import { FC, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { getFormattedUiOptions } from '../../select/helpers';
import { ControlActionValue } from '@/lib/models/policy-model-v1/controls';

interface Props {
  policy: PolicyModelV1;
  orgPolicy?: PolicyModelV1;
  formHandle: FormHandle<PolicyModelV1>;
  readonly: boolean;
  controlType: MetadataControlType;
}

const useStyles = createUseStyles((theme: Theme) => ({
  metadataControlItem: {
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      marginBottom: 8,
      paddingBottom: 8,
    },
  },
}));

const MetadataControlItemV1: FC<Props> = ({
  formHandle,
  readonly,
  policy,
  orgPolicy,
  controlType,
}) => {
  const styles = useStyles();

  const { values, setFieldValue } = formHandle;

  const handleControlChange = (
    settingType: MetadataControlType,
    entityIndex: number,
    action: ControlActionValue
  ) => {
    const updated = values.metadataControls.entities
      ? cloneDeep(values.metadataControls.entities)
      : [];

    if (entityIndex > -1) {
      updated[entityIndex].action = action;
    } else {
      updated.push({ type: settingType, action });
    }
    setFieldValue('metadataControls.entities', updated);
  };

  const handleChange = (settingType: MetadataControlType, entityIndex: number, value: string) => {
    if (settingType === 'enforce-data-privacy') {
      setFieldValue('metadataControls.forceDataPrivacy', value === 'enabled');
      return;
    }

    handleControlChange(settingType, entityIndex, value as ControlActionValue);
  };

  const actionOptionsMap = useMemo(
    () => getMetadataControlActionOptions(policy, orgPolicy),
    [policy, orgPolicy]
  );

  const toggleOptions = useMemo(
    () => getMetadataToggleActionOptions(policy, orgPolicy),
    [policy, orgPolicy]
  );

  const defaultToggleOption = useMemo(() => {
    const defaultOpts = toggleOptions.find(({ meta }) => !!meta?.default);
    return defaultOpts?.value || '';
  }, [toggleOptions]);

  // the non-license control is surfaced separately in the policy service access tab
  const control = useMemo(() => {
    return MetadataControlsSettings.find(({ type }) => type === controlType)!;
  }, [controlType]);

  if (!control) {
    return null;
  }

  const { label, description, defaultAction, actionType } = control;

  const isControl = actionType === 'control';
  const entityIndex = values.metadataControls.entities.findIndex(
    ({ type }) => type === controlType
  );
  const entity = entityIndex > -1 ? values.metadataControls.entities[entityIndex] : null;
  const baseOptions = isControl ? actionOptionsMap.get(controlType) || [] : toggleOptions;
  const options = getFormattedUiOptions(baseOptions);
  const toggleValue = values.metadataControls.forceDataPrivacy ? 'enabled' : defaultToggleOption;
  const defaultActionValue =
    typeof defaultAction === 'boolean' ? (defaultAction ? 'enabled' : 'disabled') : defaultAction;
  const value: string = isControl ? entity?.action || defaultActionValue : toggleValue;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      className={styles.metadataControlItem}
    >
      <Box pr={4}>
        <Text>{label}</Text>
        <Box mt={0.5}>
          <Text size="small" color="grey">
            {description}
          </Text>
        </Box>
      </Box>
      <Box minWidth={250}>
        <Select
          name={`metadataControls.${controlType}`}
          options={options}
          readonly={readonly}
          value={value}
          onChange={(value: string) => handleChange(controlType, entityIndex, value)}
          size="small"
        />
      </Box>
    </Stack>
  );
};
export default MetadataControlItemV1;
